/**
 * Shared stale-token recovery for every direct-CORS cell-service client
 * (assistant, entity, provenance, timeline, junkdrawer, edge-lambda) and the
 * generic `gatewayFetch`.
 *
 * Direct calls to `https://{cellId}.api.tranquility.ai/...` send the Cognito
 * access token (`AuthContext.realAccessToken`) as the Bearer. The cell Lambda
 * authorizer rejects a stale/invalid token with HTTP 403 (and the gateway can
 * answer 401). The access token is normally kept fresh by the proactive refresh
 * loop in `app/page.tsx`, but a background tab can throttle that `setTimeout`
 * and strand a stale token until the next user action — which then fails.
 *
 * `withTokenAuthRecovery` wraps a request helper so that, on an auth rejection,
 * it forces a single `/api/init` refresh (shared/coalesced), then re-runs the
 * request with the fresh token. The refreshed token is also propagated app-wide
 * (via the gateway-session event in `refreshAuthSession`) so AuthContext and
 * every other consumer recover too. When the underlying Cognito session itself
 * is dead, the user is redirected to sign-out/sign-in.
 *
 * Before sending, a token that is expired or within 30 seconds of it is swapped
 * for the current session token (TR-3773), so a throttled refresh timer does not
 * turn into a rejected request in the first place.
 *
 * `run(accessToken)` MUST rebuild the entire request from the token it is given
 * (Authorization header, and any token-derived custom headers) so the retry
 * uses the refreshed credentials rather than a stale closure.
 */

import {
  AUTH_TENANT_LOCKED_EVENT,
  AUTH_WRONG_CELL_EVENT,
  refreshAuthSession,
  type RefreshAuthResult,
} from '@/lib/refresh-auth-session';
import { resetAppNavSession } from '@/lib/app-nav-state';
import { idleClock } from '@/lib/session/idle-clock';

/** Thrown instead of issuing a cell-API call for a session that has gone idle. */
export class IdleTimeoutError extends Error {
  readonly status = 401;
  readonly code = 'IDLE_TIMEOUT';

  constructor() {
    super('Session idle timeout — request not sent');
    this.name = 'IdleTimeoutError';
  }
}

let idleSignOutStarted = false;

/**
 * Refuse cell-API traffic once the session has gone idle, and start the sign-out.
 *
 * TR-3393 defect 2: server-side idle enforcement lives in the Next proxy, whose
 * matcher only covers `/api/init`, `/api/protected/*` and `/api/proxy/*`. Calls
 * made straight to `https://{cellId}.api.{root}` — chat, the source viewer,
 * provenance, timeline — never pass through it, so a user working only in those
 * surfaces was never idle-checked at all.
 *
 * This closes the hole from the client, which is as far as this repo reaches: the
 * cell API is a separate service, so a true server-side check there needs its own
 * change. Treat this as defence in depth, not as the control of record.
 */
function assertNotIdle(): void {
  // Server-side callers (route handlers) share these modules but have no browser
  // idle clock; the proxy and the init routes enforce idle for them.
  if (typeof window === 'undefined') return;
  if (!idleClock.isExpired()) return;

  if (!idleSignOutStarted) {
    idleSignOutStarted = true;
    // Local scope: an idle timeout is a this-browser logout, so don't
    // GlobalSignOut the user off their other devices. Mirrors tpFetch.
    resetAppNavSession(window.location.pathname || '/');
    window.location.replace('/api/sign-out?scope=local');
  }

  throw new IdleTimeoutError();
}

/** Pull the numeric HTTP status off a typed *ApiError (null when absent). */
function errorStatus(error: unknown): number | null {
  const status = (error as { status?: number | null } | null | undefined)?.status;
  return typeof status === 'number' ? status : null;
}

/** A 401/403 is the only status the cell authorizer uses for a rejected token. */
function isAuthRejection(error: unknown): boolean {
  const status = errorStatus(error);
  return status === 401 || status === 403;
}

/**
 * True for a fetch cancelled via AbortSignal. Checked by `name` rather than
 * `instanceof DOMException` so it also holds for Node/undici and jsdom. A cancel
 * is never an auth or network failure, so it must not trigger a token refresh.
 */
export function isAbortError(error: unknown): boolean {
  return (error as { name?: unknown } | null | undefined)?.name === 'AbortError';
}

/**
 * A cell 403 whose gateway denial response lacks CORS headers reaches the
 * browser as an opaque `fetch` failure with **no readable status** — it is
 * indistinguishable from a genuine network blip. Each typed client wraps that
 * into an `*ApiError` with `status: null` and a `"Network error calling …"`
 * message; `gatewayFetch` lets the raw `TypeError` propagate. We treat both as
 * a *possible* stale-token rejection worth one refresh attempt.
 *
 * Excludes the clearly-offline case (`navigator.onLine === false`) so we don't
 * chase refreshes while the network is down, and excludes aborts — a cancel is
 * wrapped into that same "Network error calling …" message by the typed clients,
 * so it must be rejected explicitly, not by message shape.
 */
function isLikelyCorsOrNetworkFailure(error: unknown): boolean {
  if (errorStatus(error) !== null) return false;
  if (isAbortError(error)) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (
    error instanceof TypeError &&
    /failed to fetch|networkerror|load failed/i.test(error.message || '')
  ) {
    return true;
  }
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message.includes('Network error calling');
}

/**
 * Last access token we confirmed is the *current* session token (i.e. a refresh
 * returned the same value). A 401/403 carrying such a token is a genuine
 * authorization failure, not a stale token — so we skip the refresh for a short
 * window to avoid hammering `/api/init` on a hot 403 polling loop.
 */
let confirmedCurrentToken: string | null = null;
let confirmedAt = 0;
const CONFIRMED_TTL_MS = 30_000;
const CONFIRMED_EXPIRY_SAFETY_MS = 5_000;

/**
 * Shared result of one `/api/init` refresh. Coalesced callers all wait on the
 * same attempt, then each compares the current session token to *their* failed
 * token — a genuine 403 for the current token must not block an overlapping
 * retry that still holds an older token.
 */
type SharedRecovery =
  | { kind: 'token'; accessToken: string }
  | { kind: 'none'; reason: Exclude<RefreshAuthResult['kind'], 'refreshed'> };

/**
 * Global auth recovery coalescing — prevents multiple concurrent recovery
 * attempts when several requests fail simultaneously (e.g. during page load).
 */
let inFlightRecovery: Promise<SharedRecovery> | null = null;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * TR-3773: send a telemetry warning when a rejected call is NOT retried, so the
 * reason shows up in the tenant telemetry table instead of only the browser
 * console. The Liberal PD chat 403 was never resent and nothing recorded why.
 *
 * Lazy import: `shared/functions` reaches this module through `lib/gateway-fetch`,
 * so a static import would be a cycle. Rate-limited because the telemetry POST is
 * itself a cell call wrapped in this recovery; an unrecoverable 403 must not turn
 * into a telemetry loop.
 */
const RECOVERY_REPORT_INTERVAL_MS = 60_000;
let lastRecoveryReportAt = 0;

function reportNoRetry(reason: string): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastRecoveryReportAt < RECOVERY_REPORT_INTERVAL_MS) return;
  lastRecoveryReportAt = now;
  void import('@/shared/functions')
    .then(({ captureWarning }) => captureWarning(`[auth-recovery] cell call rejected, not retried: ${reason}`))
    .catch(() => {
      // never let instrumentation break recovery
    });
}

/**
 * Refresh before sending when the token is this close to `exp`. `/api/init`
 * rotates ahead of expiry, so this normally gets a new token; it catches the
 * cases the scheduled refresh misses (a throttled or frozen background tab, a
 * laptop waking from sleep) before the request, not after its 403.
 */
const PREFLIGHT_WINDOW_MS = 30_000;

/**
 * The last token a preflight refresh returned unchanged. `/api/init` does not
 * rotate a token too short-lived for early rotation, so without this every call
 * in its last 30 seconds would pay an extra `/api/init` round-trip for nothing.
 */
let preflightUnchangedToken: string | null = null;

function tokenIsNearExpiry(token: string): boolean {
  const exp = decodeJwtPayload(token)?.exp;
  if (typeof exp !== 'number') return false;
  return exp * 1000 - Date.now() <= PREFLIGHT_WINDOW_MS;
}

/**
 * Swap an expired or nearly expired token for the current session token before
 * the request goes out. Browser only: server-side callers have no `/api/init`
 * to call. On any refresh outcome other than a token, send the original token
 * and let the normal 401/403 recovery handle the answer.
 */
async function preflightAccessToken(accessToken: string): Promise<string> {
  if (typeof window === 'undefined') return accessToken;
  if (accessToken === preflightUnchangedToken) return accessToken;
  if (!tokenIsNearExpiry(accessToken)) return accessToken;

  console.warn('[auth-recovery] access token is expired or about to expire, refreshing before the request.');
  const outcome = await coalesceAuthRecovery();
  if (outcome.kind !== 'token') return accessToken;
  if (outcome.accessToken === accessToken) {
    preflightUnchangedToken = accessToken;
  }
  return outcome.accessToken;
}

function tokenIsSafeToTreatAsCurrent(token: string): boolean {
  const exp = decodeJwtPayload(token)?.exp;
  if (typeof exp !== 'number') return false;
  return exp * 1000 - Date.now() > CONFIRMED_EXPIRY_SAFETY_MS;
}

/**
 * Decide how to recover from a 401/403 carrying `staleAccessToken`. Returns a
 * fresh token to retry with, or `null` when there's nothing useful to retry
 * (genuine authz failure, transient refresh failure, or a logout redirect that
 * has already been triggered).
 */
async function planAuthRecovery(staleAccessToken: string): Promise<string | null> {
  if (
    staleAccessToken === confirmedCurrentToken &&
    Date.now() - confirmedAt < CONFIRMED_TTL_MS &&
    tokenIsSafeToTreatAsCurrent(staleAccessToken)
  ) {
    // Recently confirmed this exact token is current and still safely unexpired.
    console.warn(
      '[auth-recovery] skipping refresh: this token was just confirmed current ' +
        '(genuine authorization failure, not a stale token).',
    );
    reportNoRetry('token recently confirmed current');
    return null;
  }

  const outcome = await coalesceAuthRecovery();
  if (outcome.kind !== 'token') {
    reportNoRetry(`refresh outcome ${outcome.reason}`);
    return null;
  }
  if (outcome.accessToken === staleAccessToken) {
    // Session is healthy and *this* caller's token is already current → the
    // 401/403 is a genuine authorization failure. Remember it briefly so a hot
    // retry loop doesn't keep refreshing. Other coalesced callers with a
    // different (older) token still retry with `outcome.accessToken`.
    console.warn(
      '[auth-recovery] /api/init returned the SAME access token — Cognito considers it valid, ' +
        'so the cell backend is rejecting a non-expired token. A refresh/relogin will NOT help; ' +
        'check token claims (custom:env, custom:cell_id) vs the cell host being called, and the 403 body.',
    );
    confirmedCurrentToken = staleAccessToken;
    confirmedAt = Date.now();
    reportNoRetry('refresh returned the same token');
    return null;
  }
  console.warn('[auth-recovery] obtained a fresh access token → retrying the request once.');
  return outcome.accessToken;
}

/**
 * Share one in-flight `/api/init` refresh across overlapping recoveries.
 */
async function coalesceAuthRecovery(): Promise<SharedRecovery> {
  if (inFlightRecovery) {
    console.warn('[auth-recovery] coalescing with existing recovery attempt');
    return await inFlightRecovery;
  }

  inFlightRecovery = performAuthRecovery();
  try {
    return await inFlightRecovery;
  } finally {
    inFlightRecovery = null;
  }
}

/**
 * Perform the actual auth recovery — separated so concurrent callers can share
 * a single in-flight attempt. Returns the current session token when refresh
 * succeeds; callers decide per-token whether that is a retry or a no-op.
 */
async function performAuthRecovery(): Promise<SharedRecovery> {
  const refreshed = await refreshAuthSession();
  if (refreshed.kind === 'logout') {
    // Dead/unrefreshable session — bounce the user to sign-out/sign-in.
    console.warn(`[auth-recovery] session unrefreshable → redirecting to ${refreshed.href}`);
    if (typeof window !== 'undefined') {
      window.location.href = refreshed.href;
    }
    return { kind: 'none', reason: refreshed.kind };
  }
  if (refreshed.kind === 'wrong-cell') {
    // Defer to the app shell so we redirect to the correct cell (different
    // origin) or show the in-app Access Denied screen (same origin) — never a
    // sign-in bounce, which would diverge from app/page.tsx's handling.
    console.warn('[auth-recovery] /api/init reports WRONG_CELL — handing off to the app shell.');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(AUTH_WRONG_CELL_EVENT, {
          detail: { correctCellUrl: refreshed.correctCellUrl },
        }),
      );
    }
    return { kind: 'none', reason: refreshed.kind };
  }
  if (refreshed.kind === 'tenant-locked') {
    // TR-1900 incident lockout discovered mid-session (a cell call 403'd and the
    // refresh revealed the tenant is locked). Hand off to the app shell so it
    // switches to the terminal maintenance screen immediately — otherwise an
    // already-loaded shell would keep showing a broken UI, retrying failing cell
    // calls, until its next proactive /api/init refresh (up to the token's
    // remaining lifetime away). Mirrors the WRONG_CELL handoff above.
    console.warn('[auth-recovery] /api/init reports TENANT_LOCKED — handing off to the app shell.');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUTH_TENANT_LOCKED_EVENT));
    }
    return { kind: 'none', reason: refreshed.kind };
  }
  if (refreshed.kind !== 'refreshed') {
    // Transient refresh failure — caller surfaces the original rejection.
    console.warn('[auth-recovery] /api/init refresh unavailable (network/5xx) — surfacing original error.');
    return { kind: 'none', reason: refreshed.kind };
  }
  return { kind: 'token', accessToken: refreshed.accessToken };
}

/**
 * Wrap a request helper that **throws** a typed error (with `.status`) on
 * failure. On a 401/403 the token is refreshed and `run` is retried once with
 * the fresh token; otherwise the original error propagates.
 */
export async function withTokenAuthRecovery<T>(
  accessToken: string,
  run: (accessToken: string) => Promise<T>,
): Promise<T> {
  assertNotIdle();
  accessToken = await preflightAccessToken(accessToken);
  try {
    return await run(accessToken);
  } catch (error) {
    if (isAuthRejection(error)) {
      const freshToken = await planAuthRecovery(accessToken);
      if (freshToken === null) {
        throw error;
      }
      return run(freshToken);
    }

    if (isLikelyCorsOrNetworkFailure(error)) {
      // CORS-opaque cell 403 (or a network blip) — the status is unreadable, so
      // we can't be sure it's auth. Try the same refresh path anyway: if the
      // token was actually stale we get a fresh one and retry; if the session is
      // dead we redirect to re-login; otherwise the original error surfaces.
      console.warn(
        '[auth-recovery] cell-call failed with no readable status (likely a CORS-opaque 403 ' +
          'or a network error) — attempting one refresh in case the token is stale.',
      );
      const freshToken = await planAuthRecovery(accessToken);
      if (freshToken !== null) {
        return run(freshToken);
      }
      throw error;
    }

    throw error;
  }
}

/**
 * Response-returning variant for callers that don't throw on non-2xx (e.g.
 * `gatewayFetch`). On a 401/403 response the token is refreshed and `doFetch`
 * is retried once with the fresh token; otherwise the original response is
 * returned unchanged (its body is never read here, so it stays consumable).
 */
export async function fetchResponseWithAuthRecovery(
  accessToken: string,
  doFetch: (accessToken: string) => Promise<Response>,
): Promise<Response> {
  assertNotIdle();
  accessToken = await preflightAccessToken(accessToken);
  let res: Response;
  try {
    res = await doFetch(accessToken);
  } catch (error) {
    if (!isLikelyCorsOrNetworkFailure(error)) {
      throw error;
    }
    // CORS-opaque 403 / network blip — try a single refresh-and-retry.
    console.warn(
      '[auth-recovery] cell fetch failed with no readable status (likely a CORS-opaque 403 ' +
        'or a network error) — attempting one refresh in case the token is stale.',
    );
    const freshToken = await planAuthRecovery(accessToken);
    if (freshToken === null) {
      throw error;
    }
    return doFetch(freshToken);
  }

  if (res.status !== 401 && res.status !== 403) {
    return res;
  }
  const freshToken = await planAuthRecovery(accessToken);
  if (freshToken === null) {
    return res;
  }
  return doFetch(freshToken);
}
